import { BUYER_ONLY_NO_STORE_ASSOCIATE } from '../roles/roles';
import { SELF_MANAGEMENT_STATUSES, WORK_ORDER_STATUSES } from '../../constants/WorkOrderStatuses';
import { compareStrings, nullSafeGet } from '../../utils/DataAccessUtils';
import { Button, Card } from 'antd';
import React, { useMemo } from 'react';
import BuyerScheduleServiceCall from '../buyer_schedule_service_call/BuyerScheduleServiceCall';

const SelfManagementServiceCall = ({
	currentUser,
	workOrder,
	handleStartWork,
	handlePartsReceived,
	handleFinishWork,
	handlePartsOrdered,
}) => {
	const isManagerOrAdmin = currentUser.roles.some((role) =>
		BUYER_ONLY_NO_STORE_ASSOCIATE.has(role)
	);
	const isSelfManagementStatus = SELF_MANAGEMENT_STATUSES.includes(workOrder.status);
	const workIsFinished = compareStrings(workOrder.status, WORK_ORDER_STATUSES.finishedWork);
	const hasNoSupplier =
		workOrder.supplierFacility === undefined || workOrder.supplierFacility === null;
	const hasSelfManagedSupplier = compareStrings(
		nullSafeGet('buyerSupplierRelationship.relationshipStatus', workOrder),
		'SelfManaged'
	);

	const question = hasSelfManagedSupplier
		? 'Assigned to a self managed supplier. Tracking it yourself?'
		: 'Not assigned to a supplier. Tracking it yourself?';

	const canScheduleWork = useMemo(
		() =>
			hasSelfManagedSupplier &&
			[
				WORK_ORDER_STATUSES.pendingConfirmationByServiceProvider,
				WORK_ORDER_STATUSES.unassigned,
			].some((status) => compareStrings(status, workOrder.status)),
		[workOrder.status, hasSelfManagedSupplier]
	);
	const canStartWork = useMemo(
		() =>
			[
				WORK_ORDER_STATUSES.pendingConfirmationByServiceProvider,
				WORK_ORDER_STATUSES.unassigned,
				WORK_ORDER_STATUSES.plannedWork,
				WORK_ORDER_STATUSES.techScheduled,
				WORK_ORDER_STATUSES.techEnRoute,
				WORK_ORDER_STATUSES.techRescheduled,
			].some((status) => compareStrings(status, workOrder.status)),
		[workOrder.status]
	);
	const canFinishWork = useMemo(
		() =>
			[
				WORK_ORDER_STATUSES.pendingConfirmationByServiceProvider,
				WORK_ORDER_STATUSES.unassigned,
				WORK_ORDER_STATUSES.plannedWork,
				WORK_ORDER_STATUSES.startedWork,
				WORK_ORDER_STATUSES.partsOnOrder,
				WORK_ORDER_STATUSES.partsReceived,
				WORK_ORDER_STATUSES.techScheduled,
				WORK_ORDER_STATUSES.techEnRoute,
				WORK_ORDER_STATUSES.techRescheduled,
			].some((status) => compareStrings(status, workOrder.status)),
		[workOrder.status]
	);
	const canOrderParts = useMemo(
		() =>
			[
				WORK_ORDER_STATUSES.pendingConfirmationByServiceProvider,
				WORK_ORDER_STATUSES.unassigned,
				WORK_ORDER_STATUSES.plannedWork,
				WORK_ORDER_STATUSES.startedWork,
			].some((status) => compareStrings(status, workOrder.status)),
		[workOrder.status]
	);
	const canReceiveParts = useMemo(
		() =>
			[WORK_ORDER_STATUSES.partsOnOrder].some((status) => compareStrings(status, workOrder.status)),
		[workOrder.status]
	);
	if (
		!isManagerOrAdmin ||
		!isSelfManagementStatus ||
		workIsFinished ||
		!(hasNoSupplier || hasSelfManagedSupplier)
	) {
		//Not manager or admin user
		//Not a self-management status
		//Work is finished
		//Has a supplier and is externally managed supplier
		return null;
	}

	return (
		<div className="rowSpacing">
			<Card>
				<div style={{ fontSize: 16, maxWidth: 480 }}>{question}</div>
				<div>
					<div style={{ marginTop: 16, marginBottom: 40 }}>
						{canScheduleWork ? <BuyerScheduleServiceCall workOrder={workOrder} /> : null}
						{canStartWork ? (
							<Button
								type={canScheduleWork ? 'default' : 'primary'}
								key="start"
								onClick={handleStartWork}
								style={{ marginRight: 16 }}
							>
								Start Work
							</Button>
						) : null}
						{canReceiveParts ? (
							<Button
								type={
									[WORK_ORDER_STATUSES.partsOnOrder].some((status) =>
										compareStrings(status, workOrder.status)
									)
										? 'primary'
										: 'default'
								}
								key="partsReceived"
								onClick={handlePartsReceived}
								style={{ marginRight: 16 }}
							>
								Parts Received
							</Button>
						) : null}
						{canFinishWork ? (
							<Button
								type={
									[WORK_ORDER_STATUSES.startedWork, WORK_ORDER_STATUSES.partsReceived].some(
										(status) => compareStrings(status, workOrder.status)
									)
										? 'primary'
										: 'default'
								}
								key="finish"
								onClick={handleFinishWork}
								style={{ marginRight: 16 }}
							>
								Finish Work
							</Button>
						) : null}
						{canOrderParts ? (
							<Button key="partsOnOrder" onClick={handlePartsOrdered} style={{ marginRight: 16 }}>
								Parts Ordered
							</Button>
						) : null}
					</div>
				</div>
			</Card>
		</div>
	);
};

export default SelfManagementServiceCall;
